
import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Requisicao {
  _id: string
  numero: string
  insumoId: string
  nomeInsumo: string
  quantidade: number
  unidadeMedida: string
  responsavelRequisicao: string
  responsavelAprovacao: string
  obraId: string
  etapaId?: string
  centroCusto?: string
  status: 'pendente' | 'aprovada' | 'rejeitada' | 'em_cotacao' | 'cotada' | 'ordem_compra' | 'finalizada'
  dataRequisicao: string
  dataAprovacao?: string
  motivoRejeicao?: string
  observacoes?: string
  urgente: boolean
  cotacoes: Cotacao[]
  fornecedorSelecionado?: string
  valorAprovado?: number
  numeroOrdemCompra?: string
}

interface Cotacao {
  fornecedorId: string
  nomeFornecedor: string
  precoUnitario: number
  prazoEntrega: number
  condicoesPagamento: string
  observacoes?: string
  selecionado?: boolean
}

interface OrdemCompra {
  _id: string
  numero: string
  requisicaoId: string
  fornecedorId: string
  nomeFornecedor: string
  insumoId: string
  nomeInsumo: string
  quantidade: number
  precoUnitario: number
  valorTotal: number
  obraId: string
  centroCusto?: string
  status: 'emitida' | 'enviada' | 'confirmada' | 'entregue' | 'cancelada' | 'enviada_financeiro'
  dataEmissao: string
  dataEntregaPrevista: string
  condicoesPagamento: string
  observacoes?: string
  enviadaFinanceiro?: boolean
  dataEnvioFinanceiro?: string
}

export const useRequisicoes = () => {
  const [requisicoes, setRequisicoes] = useState<Requisicao[]>([])
  const [ordensCompra, setOrdensCompra] = useState<OrdemCompra[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [requisicoesData, ordensData] = await Promise.all([
        lumi.entities.requisicoes.list(),
        lumi.entities.ordens_compra.list()
      ])

      setRequisicoes(requisicoesData.list || [])
      setOrdensCompra(ordensData.list || [])
    } catch (error: any) {
      console.error('Erro ao carregar requisições:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Generate next requisition number
  const generateNextNumber = () => {
    const year = new Date().getFullYear()
    const existingNumbers = requisicoes
      .filter(r => r.numero.includes(year.toString()))
      .map(r => parseInt(r.numero.split('-').pop() || '0'))
    
    const nextNumber = Math.max(0, ...existingNumbers) + 1
    return `REQ-${year}-${nextNumber.toString().padStart(3, '0')}`
  }

  // Generate next order number
  const generateNextOrderNumber = () => {
    const year = new Date().getFullYear()
    const existingNumbers = ordensCompra
      .filter(o => o.numero.includes(year.toString()))
      .map(o => parseInt(o.numero.split('-').pop() || '0'))
    
    const nextNumber = Math.max(0, ...existingNumbers) + 1
    return `OC-${year}-${nextNumber.toString().padStart(3, '0')}`
  }

  // Create requisition
  const createRequisicao = async (data: Omit<Requisicao, '_id' | 'numero' | 'status' | 'dataRequisicao' | 'cotacoes'>) => {
    try {
      const requisicaoData = {
        ...data,
        numero: generateNextNumber(),
        status: 'pendente' as const,
        dataRequisicao: new Date().toISOString(),
        cotacoes: [],
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.requisicoes.create(requisicaoData)
      await fetchData()
      toast.success('Requisição criada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar requisição:', error)
      toast.error('Erro ao criar requisição')
      throw error
    }
  }

  // Update requisition
  const updateRequisicao = async (id: string, data: Partial<Requisicao>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.requisicoes.update(id, updateData)
      await fetchData()
      toast.success('Requisição atualizada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar requisição:', error)
      toast.error('Erro ao atualizar requisição')
      throw error
    }
  }

  // Approve requisition
  const approveRequisicao = async (id: string) => {
    try {
      await updateRequisicao(id, {
        status: 'aprovada',
        dataAprovacao: new Date().toISOString()
      })
      toast.success('Requisição aprovada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao aprovar requisição:', error)
      toast.error('Erro ao aprovar requisição')
      throw error
    }
  }

  // Reject requisition
  const rejectRequisicao = async (id: string, motivo: string) => {
    try {
      await updateRequisicao(id, {
        status: 'rejeitada',
        motivoRejeicao: motivo,
        dataAprovacao: new Date().toISOString()
      })
      toast.success('Requisição rejeitada!')
    } catch (error: any) {
      console.error('Erro ao rejeitar requisição:', error)
      toast.error('Erro ao rejeitar requisição')
      throw error
    }
  }

  // Add quotations to multiple requisitions
  const addCotacoes = async (requisicaoIds: string[], cotacoes: Cotacao[]) => {
    try {
      const updatePromises = requisicaoIds.map(id => 
        updateRequisicao(id, {
          cotacoes,
          status: 'cotada'
        })
      )

      await Promise.all(updatePromises)
      toast.success('Cotações adicionadas com sucesso!')
    } catch (error: any) {
      console.error('Erro ao adicionar cotações:', error)
      toast.error('Erro ao adicionar cotações')
      throw error
    }
  }

  // Generate purchase order
  const generateOrdemCompra = async (requisicaoId: string) => {
    try {
      const requisicao = requisicoes.find(r => r._id === requisicaoId)
      if (!requisicao) throw new Error('Requisição não encontrada')

      const cotacaoSelecionada = requisicao.cotacoes.find(c => c.selecionado)
      if (!cotacaoSelecionada) throw new Error('Nenhuma cotação selecionada')

      const numeroOC = generateNextOrderNumber()
      const valorTotal = cotacaoSelecionada.precoUnitario * requisicao.quantidade

      // Create purchase order
      const ordemData = {
        numero: numeroOC,
        requisicaoId,
        fornecedorId: cotacaoSelecionada.fornecedorId,
        nomeFornecedor: cotacaoSelecionada.nomeFornecedor,
        insumoId: requisicao.insumoId,
        nomeInsumo: requisicao.nomeInsumo,
        quantidade: requisicao.quantidade,
        precoUnitario: cotacaoSelecionada.precoUnitario,
        valorTotal,
        obraId: requisicao.obraId,
        centroCusto: requisicao.centroCusto,
        status: 'emitida' as const,
        dataEmissao: new Date().toISOString(),
        dataEntregaPrevista: new Date(Date.now() + cotacaoSelecionada.prazoEntrega * 24 * 60 * 60 * 1000).toISOString(),
        condicoesPagamento: cotacaoSelecionada.condicoesPagamento,
        observacoes: requisicao.observacoes,
        enviadaFinanceiro: false,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.ordens_compra.create(ordemData)

      // Update requisition status
      await updateRequisicao(requisicaoId, {
        status: 'ordem_compra',
        numeroOrdemCompra: numeroOC,
        fornecedorSelecionado: cotacaoSelecionada.fornecedorId,
        valorAprovado: valorTotal
      })

      await fetchData()
      toast.success('Ordem de compra gerada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao gerar ordem de compra:', error)
      toast.error('Erro ao gerar ordem de compra')
      throw error
    }
  }

  // Send order to finance
  const enviarOrdemParaFinanceiro = async (ordemId: string) => {
    try {
      const ordem = ordensCompra.find(o => o._id === ordemId)
      if (!ordem) throw new Error('Ordem de compra não encontrada')

      if (ordem.enviadaFinanceiro) {
        toast.warning('Esta ordem já foi enviada para o financeiro')
        return
      }

      // Calculate payment date based on payment terms
      const diasPagamento = ordem.condicoesPagamento.includes('30') ? 30 : 
                           ordem.condicoesPagamento.includes('15') ? 15 : 
                           ordem.condicoesPagamento.includes('45') ? 45 :
                           ordem.condicoesPagamento.includes('60') ? 60 : 0
      
      const dataVencimento = new Date(Date.now() + diasPagamento * 24 * 60 * 60 * 1000)

      // Create payment projection in finance
      await lumi.entities.contas_pagar.create({
        descricao: `Compra - ${ordem.nomeInsumo} - OC ${ordem.numero}`,
        fornecedor: ordem.nomeFornecedor,
        categoria: 'material',
        valor: ordem.valorTotal,
        vencimento: dataVencimento.toISOString(),
        status: 'pendente',
        obra_id: ordem.obraId,
        centro_custo: ordem.centroCusto,
        compra_id: ordem._id,
        creator: 'sistema',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Create cash flow projection
      await lumi.entities.fluxo_caixa.create({
        data: dataVencimento.toISOString(),
        tipo: 'saida',
        valor: ordem.valorTotal,
        categoria: 'Compras',
        descricao: `Projeção - ${ordem.nomeInsumo} - OC ${ordem.numero}`,
        origem: 'automatico',
        obra_id: ordem.obraId,
        conta_id: ordem._id,
        status: 'projecao',
        creator: 'sistema',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Update order status
      await lumi.entities.ordens_compra.update(ordemId, {
        enviadaFinanceiro: true,
        dataEnvioFinanceiro: new Date().toISOString(),
        status: 'enviada_financeiro',
        updatedAt: new Date().toISOString()
      })

      await fetchData()
      toast.success('Ordem enviada para o financeiro com sucesso!')
    } catch (error: any) {
      console.error('Erro ao enviar ordem para financeiro:', error)
      toast.error('Erro ao enviar ordem para financeiro')
      throw error
    }
  }

  // Delete requisition
  const deleteRequisicao = async (id: string) => {
    try {
      await lumi.entities.requisicoes.delete(id)
      await fetchData()
      toast.success('Requisição excluída com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir requisição:', error)
      toast.error('Erro ao excluir requisição')
      throw error
    }
  }

  // Get metrics
  const getMetrics = () => {
    const totalRequisicoes = requisicoes.length
    const pendentesAprovacao = requisicoes.filter(r => r.status === 'pendente').length
    const aprovadas = requisicoes.filter(r => r.status === 'aprovada').length
    const emCotacao = requisicoes.filter(r => r.status === 'em_cotacao').length
    const ordensEmitidas = ordensCompra.filter(o => o.status === 'emitida').length
    const valorTotalOrdens = ordensCompra.reduce((total, o) => total + o.valorTotal, 0)

    return {
      totalRequisicoes,
      pendentesAprovacao,
      aprovadas,
      emCotacao,
      ordensEmitidas,
      valorTotalOrdens
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    requisicoes,
    ordensCompra,
    loading,
    metrics: getMetrics(),
    fetchData,
    createRequisicao,
    updateRequisicao,
    approveRequisicao,
    rejectRequisicao,
    addCotacoes,
    generateOrdemCompra,
    enviarOrdemParaFinanceiro,
    deleteRequisicao
  }
}
